/**
 * PSTUDIO-4242: timeout for the two bare XMLHttpRequests a validation pass
 * awaits (`uploadCsv`, `validateCsv`). An XHR has no timeout by default, so a
 * silently dropped connection never fires `onload` or `onerror` and the pass
 * never ends — and `isValidating` is cleared only when the LATEST pass ends,
 * so the spinner, the Validate button and a queued Publish click would hang
 * until a reload.
 *
 * An IDLE timeout, not XHR's own `timeout`: that one is a deadline from
 * `send()` which progress does not move, so it would cut off a large
 * solution's CSV still uploading over a slow but live link. Once the upload
 * is done no event fires until the answer starts, so this is also how long
 * the server gets to compile.
 */
export const CSV_REQUEST_TIMEOUT_MS = 120_000;

/**
 * Aborts `request` and calls `onIdle` after `idleMs` with no upload or
 * download progress. Call it just before `send()`. Listeners, not `on*`
 * handlers, so the caller's own `onprogress` stays its own.
 */
export const abortWhenIdle = (
  request: XMLHttpRequest,
  onIdle: () => void,
  idleMs = CSV_REQUEST_TIMEOUT_MS
): void => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const disarm = () => clearTimeout(timer);
  const arm = () => {
    disarm();
    timer = setTimeout(() => {
      request.removeEventListener('loadend', disarm);
      request.abort();
      onIdle();
    }, idleMs);
  };

  request.upload.addEventListener('progress', arm);
  request.addEventListener('progress', arm);
  request.addEventListener('loadend', disarm);
  arm();
};
